import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faUser, faEnvelope, faPhone, faLock } from '@fortawesome/free-solid-svg-icons';
import { registerUser, sendOTP, googleAuth } from '../services/api';
import GoogleAuthButton from '../components/GoogleAuthButton';

const RegisterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const HomeLink = styled(Link)`
  position: fixed;
  top: 16px;
  left: 16px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  font-size: 14px;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 6px 12px;
  }
`;

const RegisterCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: 24px;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 450px;
  margin: 20px auto;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 480px) {
    padding: 20px;
    gap: 16px;
    margin: 10px auto;
    border-radius: 16px;
    max-width: 375px;
  }

  @media (max-width: 360px) {
    padding: 16px;
    gap: 12px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  text-align: center;
  color: #ff6b6b;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 12px;
    margin: 0 0 4px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #444;
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
  box-sizing: border-box;

  &:focus {
    border-color: #ff6b6b;
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.1);
  }

  &.error {
    border-color: #ff4646;
    box-shadow: 0 0 0 2px rgba(255, 70, 70, 0.1);
  }

  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 0 10px;
  }
`;

const InputIcon = styled.span`
  position: absolute;
  left: clamp(10px, 1.5vw, 12px);
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: clamp(14px, 1.8vw, 16px);
`;

const ErrorText = styled.span`
  color: #ff4646;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const SubmitButton = styled.button`
  background: #ff6b6b;
  color: white;
  height: 40px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;

  &:hover {
    background: #ff8e53;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    margin-top: 4px;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
  color: #757575;
  font-size: 12px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }

  @media (max-width: 480px) {
    margin: 8px 0;
    font-size: 11px;
    gap: 8px;
  }
`;

const GoogleSection = styled.div`
  margin: 8px 0;

  @media (max-width: 480px) {
    margin: 4px 0;
  }
`;

const LoginLink = styled(Link)`
  color: #ff6b6b;
  text-align: center;
  display: block;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 6px;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 8px;

  &:hover {
    color: #ff8e53;
    background: rgba(255, 107, 107, 0.1);
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin-top: 4px;
    padding: 4px;
  }
`;

const RequiredAsterisk = styled.span`
  color: #ff4646;
  margin-left: 2px;
  font-size: 0.9em;
`;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
    password?: string;
  }>({});

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      email?: string;
      phone?: string;
      password?: string;
    } = {};

    if (!formData.name) {
      newErrors.name = "Oops! Name field can't be empty.";
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters long.';
    }

    if (!formData.email) {
      newErrors.email = 'Oops! You forgot to add your email.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone) {
      newErrors.phone = 'Oops! Phone number is missing. ';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.password) {
      newErrors.password = 'Oops! You forgot to enter your password.';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Too short — your password must be 6 characters minimum.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password = 'Your password needs to be stronger: add a capital letter, a lowercase letter, a number, and a special symbol.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const response = await registerUser(formData);
      console.log("🚀 ~ handleSubmit ~ response:", response)
      const userId = response.data._id;

      // Send OTP after successful registration
      await sendOTP({
        userId,
        email: formData.email
      });

      // Navigate to OTP verification page with required data
      navigate('/verify-otp', {
        state: {
          userId,
          email: formData.email
        }
      });

      toast.success('Registration successful! Please verify your email.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      console.log("Google credential received:", credentialResponse);
      const response = await googleAuth(credentialResponse.credential);
      console.log("Google auth response:", response);

      if (!response) {
        console.error("No response received from googleAuth");
        toast.error('Registration failed: No response from server');
        return;
      }

      const user = response.data.user;
      const token = user.token;

      if (!token) {
        console.error("No token in response data:", response.data);
        toast.error('Registration failed: No token received');
        return;
      }

      if (!user) {
        console.error("No user in response data:", response.data);
        toast.error('Registration failed: No user data received');
        return;
      }

      // Store the token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      console.log("Token and user data stored successfully");
      toast.success('Google registration successful!');

      // Force a hard navigation to dashboard
      window.location.href = '/dashboard';

    } catch (error) {
      console.error('Google registration error:', error);
      toast.error(`Google registration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <RegisterContainer>
      <HomeLink to="/">
        <FontAwesomeIcon icon={faHome} /> Back to Home
      </HomeLink>
      <RegisterCard>
        <Title>Create Account</Title>
        <Subtitle>Join us and start your journey today</Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faUser} /> Full Name<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={errors.name ? 'error' : ''}
              />
            </InputWrapper>
            {errors.name && <ErrorText>{errors.name}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faEnvelope} /> Email<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className={errors.email ? 'error' : ''}
              />
            </InputWrapper>
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faPhone} /> Phone Number<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                className={errors.phone ? 'error' : ''}
              />
            </InputWrapper>
            {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faLock} /> Password<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                className={errors.password ? 'error' : ''}
              />
            </InputWrapper>
            {errors.password && <ErrorText>{errors.password}</ErrorText>}
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </SubmitButton>
        </Form>

        <Divider>or continue with</Divider>

        <GoogleSection>
          <GoogleAuthButton onSuccess={handleGoogleSuccess} text="signup_with" />
        </GoogleSection>

        <LoginLink to="/login">
          Already have an account? Sign in
        </LoginLink>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register; 